import { JetView } from 'webix-jet'; // Importing JetView from webix-jet for creating views
import { devConfig } from 'config/config'; // Importing development configuration

/**
 * Class representing the TableReferralsReportView.
 * @extends JetView
 */
export default class TableReferralsReportView extends JetView {
    /**
     * Configures the view layout and components.
     * @returns {Object} The configuration object for the view.
     */
    config() {
        const _ = this.app.getService('locale')._; // Localization service
        const dateFormat = webix.Date.dateToStr(this.app.config.dateFormat); // Date formatting function
        const cur_user = this.getParam('user', true); // Current user parameter

        // Configuration for the total report datatable
        const totalReport = {
            view: 'datatable', // Datatable view
            id: 'datatableTotal', // Unique ID for the datatable
            css: 'webix_header_border webix_data_border webix_footer_border', // CSS classes for styling
            select: true, // Enable row selection
            columns: [
                // Column definitions
                {
                    id: 'id',
                    header: _('ID Referente'), // Header text
                    width: 200,
                    sort: 'int', // Sorting type
                    exportType: 'number', // Export type
                },
                {
                    id: 'name',
                    header: _('Nombre'),
                    width: 200,
                    sort: 'int',
                    exportType: 'number',
                    hidden: !devConfig.getPermAction(
                        'sensitiveInformationNames',
                        'Add', // Conditional visibility based on permissions
                    ),
                },
                {
                    id: 'TotalReferrals',
                    header: _('Referidos Totales'),
                    width: 200,
                    sort: 'int',
                    exportType: 'number',
                },
                {
                    id: 'SuccessfulReferrals',
                    header: _('Referidos Exitosos'),
                    width: 200,
                    sort: 'int',
                    exportType: 'number',
                },
                {
                    id: 'DepositBonuses',
                    header: _('Bonos por Deposito'),
                    width: 200,
                    sort: 'int',
                    exportType: 'number',
                },
                {
                    id: 'BetBonuses',
                    name: 'BetBonuses',
                    header: _('Bonos por Apuesta'),
                    width: 200,
                    sort: 'int',
                    exportType: 'number',
                },
            ],
            on: {
                // Event handlers for the datatable
                onBeforeLoad: function () {
                    // Before loading data
                    this.showOverlay(
                        "<div id='contenedor'; class='container-preload'><div style='position: fixed; top: calc(50% - 57px);left: calc(50% - 60px);width: 120px;' class='progress'><div class='icon-preload girando'></div><div class='load-text'>Loading... </div></div></div>",
                    );
                },
                onAfterLoad: function () {
                    // After loading data
                    this.hideOverlay();
                },
            },
            ready: function () {
                // When the datatable is ready
                if (!this.count()) {
                    // Check if there are no records
                    webix.extend(this, webix.OverlayBox); // Extend with overlay functionality
                    this.showOverlay(
                        "<div class='no-data' style='margin:75px; font-size:18px;'>" +
                            _('No hay datos') + // No data message
                            '</div>',
                    );
                }
            },
            datafetch: devConfig.getDefaultDataFetch(), // Default data fetch configuration
            pager: 'pagerB', // Pager ID for pagination
        };

        // Configuration for the detail report datatable
        const detailReport = {
            view: 'datatable', // Datatable view
            id: 'datatableDetails', // Unique ID for the datatable
            css: 'webix_header_border webix_data_border webix_footer_border', // CSS classes for styling
            select: true, // Enable row selection
            hidden: true, // Initially hidden
            columns: [
                // Column definitions
                {
                    id: 'ReferralId',
                    header: _('ID Referido'),
                    width: 100,
                    sort: 'int',
                },
                {
                    id: 'DateRegister',
                    header: _('Fecha de Registro'),
                    fillspace: 0,
                    minWidth: 0,
                    width: 150,
                    sort: 'text',
                    format: dateFormat, // Date formatting
                },
                {
                    id: 'Name',
                    header: _('Nombre'),
                    width: 150,
                    sort: 'text',
                    hidden: !devConfig.getPermAction(
                        'sensitiveInformationNames',
                        'Add', // Conditional visibility based on permissions
                    ),
                },
                {
                    id: 'Lastname',
                    header: _('Apellido'),
                    width: 150,
                    sort: 'text',
                    hidden: !devConfig.getPermAction(
                        'sensitiveInformationSurnames',
                        'Add', // Conditional visibility based on permissions
                    ),
                },
                {
                    id: 'Status',
                    header: _('Estado'),
                    width: 150,
                    sort: 'text',
                    template: function (obj) {
                        // Template for status display
                        return obj.Status == 'A' ? 'Activo' : obj.Status == 'I' ? 'Desactivado' : null;
                    },
                },
                {
                    id: 'DateLastLogin',
                    header: _('Ultimo login'),
                    fillspace: 0,
                    minWidth: 0,
                    width: 150,
                    sort: 'text',
                    format: dateFormat, // Date formatting
                },
                {
                    id: 'Referrer',
                    header: _('Referente'),
                    width: 100,
                    sort: 'int',
                },
                {
                    id: 'DateFirstDeposit',
                    header: _('Primer Deposito'),
                    fillspace: 0,
                    minWidth: 0,
                    width: 150,
                    sort: 'text',
                    format: dateFormat, // Date formatting
                },
                {
                    id: 'AmountFirstDeposit',
                    header: _('Valor Primer Deposito'),
                    width: 200,
                    sort: 'int',
                    format: webix.i18n.priceFormat, // Price formatting
                    exportType: 'number',
                    footer: { content: 'mySummColumn' }, // Footer configuration
                    editor: 'text', // Editor type
                },
                {
                    id: 'AmountSportBet',
                    header: _('Valor Apuestas Deportivas'),
                    width: 200,
                    sort: 'int',
                    format: webix.i18n.priceFormat, // Price formatting
                    exportType: 'number',
                    footer: { content: 'mySummColumn' }, // Footer configuration
                    editor: 'text', // Editor type
                },
                {
                    id: 'DateLastSportBet',
                    header: _('Ultima Apuesta Deportiva'),
                    fillspace: 0,
                    minWidth: 0,
                    width: 200,
                    sort: 'text',
                    format: dateFormat, // Date formatting
                },
                {
                    id: 'AmountCasinoBet',
                    header: _('Valor Apuesta Casino'),
                    width: 200,
                    sort: 'int',
                    format: webix.i18n.priceFormat, // Price formatting
                    exportType: 'number',
                    footer: { content: 'mySummColumn' }, // Footer configuration
                    editor: 'text', // Editor type
                },
                {
                    id: 'DateLastCasinoBet',
                    header: _('Ultima Apuesta Casino'),
                    fillspace: 0,
                    minWidth: 0,
                    width: 200,
                    sort: 'text',
                    format: dateFormat, // Date formatting
                },
                {
                    id: 'FirstDepositAward',
                    header: _('Premio Primer Deposito'),
                    width: 200,
                    sort: 'int',
                    template: function (obj) {
                        // Template for first deposit award display
                        return obj.FirstDepositAward == '1'
                            ? 'Genero premio'
                            : obj.FirstDepositAward == '0'
                              ? 'No ha generado premio'
                              : null;
                    },
                },
                {
                    id: 'BettingAward',
                    header: _('Premio Apuestas'),
                    width: 200,
                    sort: 'int',
                    template: function (obj) {
                        // Template for betting award display
                        return obj.BettingAward == '1'
                            ? 'Genero premio'
                            : obj.BettingAward == '0'
                              ? 'No ha genero premio'
                              : null;
                    },
                },
                {
                    id: 'TotalReferrals',
                    header: _('Total Referidos'),
                    width: 100,
                    sort: 'text',
                },
            ],
            on: {
                // Event handlers for the datatable
                onBeforeLoad: function () {
                    // Before loading data
                    this.showOverlay(
                        "<div id='contenedor' class='container-preload'><div style='position: fixed; top: calc(50% - 57px);left: calc(50% - 60px);width: 120px;' class='progress'><div class='icon-preload girando'></div><div class='load-text'>Loading... </div></div></div>",
                    );
                },
                onAfterLoad: function () {
                    // After loading data
                    this.hideOverlay();
                },
            },
            ready: function () {
                // When the datatable is ready
                if (!this.count()) {
                    // Check if there are no records
                    webix.extend(this, webix.OverlayBox); // Extend with overlay functionality
                    this.showOverlay(
                        "<div class='no-data' style='margin:75px; font-size:18px;'>" +
                            _('No hay datos') + // No data message
                            '</div>',
                    );
                }
            },
            datafetch: devConfig.getDefaultDataFetch(), // Default data fetch configuration
            pager: 'pagerA', // Pager ID for pagination
        };

        // Return the layout structure containing both reports
        return {
            rows: [totalReport, detailReport], // Rows containing both datatables
        };
    }

    /**
     * Initializes the view and sets up event listeners.
     */
    init() {
        const cur_user = this.getParam('user', true); // Current user parameter
        var url = devConfig.geturlApi() + 'es/Report/GetReferentReport'; // API endpoint for report
        var filter = ''; // Filter string for API requests

        // Event listener for filtering data in the reports
        this.on(this.app, 'referralsReportPlayerMain:filter', (filterData) => {
            var object = Object.entries(filterData); // Convert filter data to entries
            var string1 = ''; // Initialize filter string
            for (var i = 0; i < object.length; i++) {
                // Iterate through filter entries
                if (typeof object[i][1] !== 'object' && object[i][1] !== '') {
                    // Check for non-object and non-empty values
                    if (object[i][0] == 'DatepickerRegister') {
                        // Date range for registration
                        var date = object[i][1].split(' - ');
                        string1 = string1 + '&dateFrom=' + date[0];
                        string1 = string1 + '&dateTo=' + date[1];
                    } else {
                        if (object[i][0] == 'DatepickerDeposit') {
                            // Date range for deposit
                            var date = object[i][1].split(' - ');
                            string1 = string1 + '&dateFrom2=' + date[0];
                            string1 = string1 + '&dateTo2=' + date[1];
                        } else {
                            if (
                                object[i][0] != 'Sort' &&
                                object[i][0] != 'Dir' &&
                                object[i][0] != 'Date' &&
                                object[i][0] != 'Date2'
                            ) {
                                string1 = string1 + '&' + object[i][0] + '=' + object[i][1]; // Append other filter parameters
                            } else {
                                if (object[i][0] == 'Date') {
                                    // Single date filter
                                    var date = object[i][1].replace(' 00:00:00', '');
                                    string1 = string1 + '&dateFrom=' + date;
                                    string1 = string1 + '&dateTo=' + date;
                                }
                                if (object[i][0] == 'Date2') {
                                    // Second date filter
                                    var date = object[i][1].replace(' 00:00:00', '');
                                    string1 = string1 + '&dateFrom2=' + date;
                                    string1 = string1 + '&dateTo2=' + date;
                                }
                            }
                        }
                    }
                } else {
                    if (typeof object[i][1] === 'object') {
                        // Handle object type filters
                        var object2 = Object.entries(object[i][1]);
                        for (var j = 0; j < object2.length; j++) {
                            if (object2[j][1] !== null) {
                                if (object2[j][0] === 'start') {
                                    // Start date filter
                                    var d = new Date(object2[j][1]);
                                    var dateFrom =
                                        'dateFrom=' +
                                        d.getFullYear() +
                                        ':' +
                                        ('00' + (d.getMonth() + 1)).slice(-2) +
                                        ':' +
                                        ('00' + d.getDate()).slice(-2) +
                                        ' ' +
                                        ('00' + d.getHours()).slice(-2) +
                                        ':' +
                                        ('00' + d.getMinutes()).slice(-2) +
                                        ':' +
                                        ('00' + d.getSeconds()).slice(-2);
                                    string1 = string1 + '&' + dateFrom; // Append start date
                                } else {
                                    if (object2[j][0] === 'end') {
                                        // End date filter
                                        var d = new Date(object2[j][1]);
                                        var dateTo =
                                            'dateTo=' +
                                            d.getFullYear() +
                                            ':' +
                                            ('00' + (d.getMonth() + 1)).slice(-2) +
                                            ':' +
                                            ('00' + d.getDate()).slice(-2) +
                                            ' ' +
                                            ('00' + d.getHours()).slice(-2) +
                                            ':' +
                                            ('00' + d.getMinutes()).slice(-2) +
                                            ':' +
                                            ('00' + d.getSeconds()).slice(-2);
                                        string1 = string1 + '&' + dateTo; // Append end date
                                    }
                                }
                            }
                        }
                    }
                }
            }
            if (filterData.Type === '1') {
                // If type is 1, load total report
                $$('datatableDetails').hide(); // Hide detail report
                $$('datatableTotal').show(); // Show total report
                $$('datatableTotal').clearAll(); // Clear previous data
                $$('toolbar').hide(); // Hide toolbar
                $$('toolbar2').show(); // Show secondary toolbar
                $$('datatableTotal').load(url + '?start=0&count=10' + string1); // Load data with filters
                filter = string1; // Store filter string
            } else if (filterData.Type === '0') {
                // If type is 0, load detail report
                $$('datatableTotal').hide(); // Hide total report
                $$('datatableDetails').show(); // Show detail report
                $$('datatableDetails').clearAll(); // Clear previous data
                $$('toolbar').show(); // Show toolbar
                $$('toolbar2').hide(); // Hide secondary toolbar
                $$('datatableDetails').load(
                    url + '?start=0&count=' + $$('datatableDetails').data.$pagesize + string1, // Load data with filters
                );
                filter = string1; // Store filter string
            } else {
                console.error('El componente no existe.'); // Log error if component does not exist
            }
        });
    }
}

// FILE DOCUMENTED
